import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import NeoInput from "./NeoInput";

/**
 * NeoDatePicker
 * Componente premium de seleção de data nativo sob o estilo Taça Outono.
 * - Input base com ícone de calendário outonal.
 * - Calendário popup completo com grade dinâmica de dias e suporte a anos.
 * - Padrão brasileiro de exibição (DD/MM/YYYY) e salvamento no padrão ISO (YYYY-MM-DD).
 * - Fechamento inteligente ao clicar fora ou ao selecionar uma data.
 */
const NeoDatePicker = ({
    label = "",
    value = "", // formato ISO 'YYYY-MM-DD'
    onChange, // callback(dateString)
    placeholder = "Selecione a data...",
    disabled = false,
    error = "",
    size = "md",
    className = "",
    maxDate = "", // data máxima opcional
    minDate = "", // data mínima opcional
    ...props
}) => {
    const wrapperRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [displayValue, setDisplayValue] = useState("");
    
    // Estados internos do calendário
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    // Mapeamento de nomes de meses em português
    const MONTH_NAMES = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    // Sincronizar valor recebido externamente
    useEffect(() => {
        if (value) {
            const parts = value.split("-");
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const day = parseInt(parts[2], 10);
                
                const parsedDate = new Date(year, month, day);
                setSelectedDate(parsedDate);
                setCurrentDate(parsedDate);
                
                // Formata exibição brasileira
                const formattedDay = String(day).padStart(2, "0");
                const formattedMonth = String(month + 1).padStart(2, "0");
                setDisplayValue(`${formattedDay}/${formattedMonth}/${year}`);
                return;
            }
        }
        setSelectedDate(null);
        setDisplayValue("");
    }, [value]);

    // Fechar ao clicar fora do componente
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleInputFocus = () => {
        if (disabled) return;
        setIsOpen(true);
    };

    // Cálculos de geração de dias do calendário
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const totalDays = getDaysInMonth(year, month);
    const firstDayIndex = getFirstDayOfMonth(year, month);

    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

    // Lista de dias a serem renderizados no grid (7x6 = 42 dias)
    const days = [];

    // Dias do mês anterior (vizinhos)
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push({
            day: daysInPrevMonth - i,
            month: prevMonth,
            year: prevYear,
            isNeighbor: true
        });
    }

    // Dias do mês atual
    for (let i = 1; i <= totalDays; i++) {
        days.push({
            day: i,
            month,
            year,
            isNeighbor: false
        });
    }

    // Dias do próximo mês (vizinhos)
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
        days.push({
            day: i,
            month: nextMonth,
            year: nextYear,
            isNeighbor: true
        });
    }

    // Ações de Navegação
    const changeMonth = (offset) => {
        const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1);
        setCurrentDate(newDate);
    };

    const changeYear = (offset) => {
        const newDate = new Date(currentDate.getFullYear() + offset, currentDate.getMonth(), 1);
        setCurrentDate(newDate);
    };

    const selectDay = (dayObj) => {
        if (disabled) return;
        
        const dayStr = String(dayObj.day).padStart(2, "0");
        const monthStr = String(dayObj.month + 1).padStart(2, "0");
        const dateISO = `${dayObj.year}-${monthStr}-${dayStr}`;

        if (onChange) {
            onChange(dateISO); // Callback em formato ISO (YYYY-MM-DD)
        }
        
        setIsOpen(false);
    };

    const isSelected = (dayObj) => {
        if (!selectedDate) return false;
        return selectedDate.getDate() === dayObj.day &&
               selectedDate.getMonth() === dayObj.month &&
               selectedDate.getFullYear() === dayObj.year;
    };

    const isToday = (dayObj) => {
        const today = new Date();
        return today.getDate() === dayObj.day &&
               today.getMonth() === dayObj.month &&
               today.getFullYear() === dayObj.year;
    };

    return (
        <div ref={wrapperRef} className={`neo-datepicker-wrapper ${className}`}>
            {/* Input Gatilho (Reutiliza Estilos do NeoInput) */}
            <NeoInput
                label={label}
                placeholder={placeholder}
                value={displayValue}
                onFocus={handleInputFocus}
                readOnly={true} // Obriga a selecionar pelo calendário
                disabled={disabled}
                error={error}
                size={size}
                icon="mdi-calendar-month-outline"
                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                inputStyle={{ cursor: disabled ? "not-allowed" : "pointer" }}
                {...props}
            />

            {/* Painel do Calendário Flutuante */}
            {isOpen && !disabled && (
                <div className="neo-datepicker-calendar">
                    {/* Cabeçalho do Calendário (Navegação de Meses e Anos) */}
                    <div className="neo-datepicker-header">
                        {/* Retroceder Ano e Mês */}
                        <div className="d-flex gap-1">
                            <button 
                                type="button" 
                                className="neo-datepicker-header-btn" 
                                onClick={() => changeYear(-1)}
                                title="Ano Anterior"
                            >
                                <i className="mdi mdi-chevron-double-left font-size-14"></i>
                            </button>
                            <button 
                                type="button" 
                                className="neo-datepicker-header-btn" 
                                onClick={() => changeMonth(-1)}
                                title="Mês Anterior"
                            >
                                <i className="mdi mdi-chevron-left font-size-14"></i>
                            </button>
                        </div>

                        {/* Mês e Ano Atual */}
                        <h6 className="neo-datepicker-month-title">
                            {MONTH_NAMES[month]} {year}
                        </h6>

                        {/* Avançar Mês e Ano */}
                        <div className="d-flex gap-1">
                            <button 
                                type="button" 
                                className="neo-datepicker-header-btn" 
                                onClick={() => changeMonth(1)}
                                title="Próximo Mês"
                            >
                                <i className="mdi mdi-chevron-right font-size-14"></i>
                            </button>
                            <button 
                                type="button" 
                                className="neo-datepicker-header-btn" 
                                onClick={() => changeYear(1)}
                                title="Próximo Ano"
                            >
                                <i className="mdi mdi-chevron-double-right font-size-14"></i>
                            </button>
                        </div>
                    </div>

                    {/* Dias da Semana */}
                    <div className="neo-datepicker-weekdays">
                        <span className="neo-datepicker-weekday">D</span>
                        <span className="neo-datepicker-weekday">S</span>
                        <span className="neo-datepicker-weekday">T</span>
                        <span className="neo-datepicker-weekday">Q</span>
                        <span className="neo-datepicker-weekday">Q</span>
                        <span className="neo-datepicker-weekday">S</span>
                        <span className="neo-datepicker-weekday">S</span>
                    </div>

                    {/* Grade de Dias */}
                    <div className="neo-datepicker-days-grid">
                        {days.map((dayObj, idx) => {
                            const isSel = isSelected(dayObj);
                            const isTdy = isToday(dayObj);
                            
                            const cellClass = [
                                "neo-datepicker-day",
                                dayObj.isNeighbor ? "neo-datepicker-day-neighbor" : "",
                                isSel ? "neo-datepicker-day-selected" : "",
                                isTdy ? "neo-datepicker-day-today" : ""
                            ].filter(Boolean).join(" ");

                            return (
                                <div
                                    key={idx}
                                    className={cellClass}
                                    onClick={() => selectDay(dayObj)}
                                >
                                    {dayObj.day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

NeoDatePicker.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    className: PropTypes.string,
    maxDate: PropTypes.string,
    minDate: PropTypes.string
};

export default NeoDatePicker;
